import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiCalendar, FiArrowRight, FiCheckCircle, FiZap, FiMessageSquare, FiSend } = FiIcons;

const ChatSimulation = () => {
  const [messages, setMessages] = useState([]);
  const [key, setKey] = useState(0);

  const conversation = [
    { sender: 'ai', text: "Hi! I'm the AI assistant for Handvantage. How can I help today?" },
    { sender: 'user', text: "I'm interested in the coaching program. What's the investment?" },
    { sender: 'ai', text: "Great question! To give you the right info, are you currently making over $10k/month in revenue?" },
    { sender: 'user', text: "Yes, we are." },
    { sender: 'ai', text: "Perfect. The investment is $1,200 for setup. It's designed for established coaches ready to scale." },
    { sender: 'ai', text: "Based on what you've said, you sound like a great fit. Would you like to book a free strategy call to discuss your goals?" },
    { sender: 'cta', text: "Book a Free Strategy Call" }
  ];
  
  useEffect(() => {
    const timeouts = [];
    
    const runConversation = () => {
      setMessages([]);
      let currentMessages = [];
      conversation.forEach((msg, index) => {
        const timeout = setTimeout(() => {
          currentMessages = [...currentMessages, { ...msg, id: `${key}-${index}` }];
          setMessages(currentMessages);
        }, index * 2200);
        timeouts.push(timeout);
      });
    };

    runConversation();

    const loopInterval = setInterval(() => {
      setKey(prevKey => prevKey + 1);
    }, conversation.length * 2200 + 3000);

    return () => {
      timeouts.forEach(clearTimeout);
      clearInterval(loopInterval);
    };
  }, [key]);

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-4 max-w-md mx-auto relative h-[500px] flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-200 pb-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
          <SafeIcon icon={FiMessageSquare} className="text-white text-2xl" />
        </div>
        <div>
          <p className="font-bold text-gray-900 text-lg">AI Sales Assistant</p>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse"></div>
            <p className="text-sm text-gray-500">Online</p>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-grow overflow-hidden relative">
        <div className="absolute inset-0 overflow-y-auto p-2 space-y-4">
          <AnimatePresence>
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                layout
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.sender === 'cta' ? (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    className="bg-blue-600 text-white font-bold py-3 px-5 rounded-xl shadow-lg"
                  >
                    {msg.text}
                  </motion.button>
                ) : (
                  <div className={`max-w-xs lg:max-w-sm px-4 py-3 rounded-2xl shadow-md ${
                    msg.sender === 'user' 
                      ? 'bg-blue-500 text-white rounded-br-lg' 
                      : 'bg-gray-100 text-gray-800 rounded-bl-lg'
                  }`}>
                    <p className="text-sm">{msg.text}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Input Area */}
      <div className="border-t border-gray-200 pt-3 mt-4">
        <div className="bg-gray-100 rounded-lg flex items-center px-4 py-2">
          <input 
            type="text" 
            placeholder="AI is qualifying a lead..." 
            disabled 
            className="flex-grow bg-transparent text-sm text-gray-600 outline-none cursor-not-allowed"
          />
          <SafeIcon icon={FiSend} className="text-gray-400 text-lg" />
        </div>
      </div>
    </div>
  );
};


const HeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 min-h-screen flex items-center overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-700"></div>
      </div>
      
      <div className="container mx-auto px-6 relative z-10 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-red-500/20 border border-red-400/30 rounded-full px-4 py-2 mb-6"
            >
              <SafeIcon icon={FiZap} className="text-yellow-400" />
              <span className="text-sm font-semibold text-red-200">⚡ Black Friday: Ends November 15th - Only 13 Spots Left</span>
            </motion.div>

            <h1 className="text-5xl lg:text-7xl font-bold leading-tight mb-6">
              Stop Wasting <span className="text-yellow-400 relative">
                10+ Hours
                <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 200 8">
                  <path d="M0 4 Q50 0, 100 4 T200 4" stroke="currentColor" strokeWidth="3" fill="none" className="text-yellow-400"/>
                </svg>
              </span> a Week on Unqualified Leads
            </h1>
            
            <h2 className="text-2xl lg:text-4xl font-semibold mb-6 text-blue-200">
              Start Closing <span className="text-green-400 font-bold">35% More</span> High-Ticket Clients
            </h2>
            
            <p className="text-xl leading-relaxed mb-8 text-gray-300">
              Get a <span className="text-white font-semibold">Done-For-You AI Assistant</span> that pre-qualifies every lead, 
              books only high-intent prospects on your calendar, and works 24/7 to fill your pipeline—so you can focus on what you do best: <span className="text-yellow-400">coaching</span>.
            </p>

            {/* Quick Benefits */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                'Save 10+ Hours Weekly',
                '35% Higher Close Rate',
                'Works 24/7 Automatically',
                'Setup in 7 Days'
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <SafeIcon icon={FiCheckCircle} className="text-green-400 flex-shrink-0" />
                  <span className="text-sm text-gray-300">{benefit}</span>
                </motion.div>
              ))}
            </div>
            
            <div className="space-y-4">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(251, 146, 60, 0.4)" }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-orange-500 via-red-500 to-red-600 text-white px-10 py-5 rounded-xl text-xl font-bold shadow-2xl transition-all duration-300 flex items-center gap-3 w-full lg:w-auto justify-center group relative overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-orange-400 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <SafeIcon icon={FiCalendar} className="text-2xl relative z-10" />
                <span className="relative z-10">BOOK A FREE STRATEGY CALL</span>
                <SafeIcon icon={FiArrowRight} className="text-xl relative z-10 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              
              <div className="flex items-center justify-center lg:justify-start gap-2">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 border-2 border-slate-900"></div>
                  ))}
                </div>
                <p className="text-sm text-blue-200">
                  <span className="font-bold text-yellow-400">42 coaches</span> booked this week
                </p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                <p className="text-center lg:text-left text-sm text-blue-200">
                  🔥 <span className="font-bold text-white">Limited-Time Black Friday Pricing:</span> Get $400 OFF{' '}
                  <span className="text-yellow-400 font-bold text-lg">$800</span>{' '}
                  <span className="line-through text-gray-400">$1,200</span>
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right Chat Simulation */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            {/* Glow Effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-2xl opacity-20 animate-pulse"></div>
            
            <ChatSimulation />

          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;